"use client";

import { type KeyboardEvent, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import Link from "next/link";
import { useRouter } from "next/navigation";

import {
  Header,
  InputComboBox,
  ListBoxItem,
  ListBoxSection,
} from "@jetblue/kit/input-combobox";
import { PairedButton } from "@jetblue/kit/paired-button";

export type InputSearchProperties<T extends string = string> = {
  labels: {
    heading: string;
    placeholder: string;
  };
  primaryLink: {
    href: string;
    isExternal: boolean;
    label: string;
    target: "_self" | "_blank";
  };
  quickLinks: {
    label: string;
    links?:
      | ({
          "@nodes": T[];
        } & Record<
          T,
          {
            href: string;
            isExternal: boolean;
            label: string;
            target?: "_self" | "_blank" | undefined;
          }
        >)
      | undefined;
  };
  theme?: "light" | "dark";
  initialSearch?: string;
};

const appendSearchQueryString = ({
  href,
  search,
}: {
  href: string;
  search: string;
}): string => {
  const url =
    !href.startsWith("http://") && !href.startsWith("https://")
      ? new URL(href, "https://www.jetblue.com")
      : new URL(href);

  const trimmedSearch = search.trim();

  if (trimmedSearch) {
    url.searchParams.set("search", trimmedSearch);
  }

  return url.href;
};

export const InputSearch = ({
  className,
  labels,
  primaryLink,
  quickLinks,
  theme,
  initialSearch = "",
}: InputSearchProperties & {
  className?: string | undefined;
}) => {
  const router = useRouter();
  const [inputValue, setInputValue] = useState(initialSearch);
  const activeDescendantOnEnterReference = useRef<string | null>(null);
  const inputValueOnEnterReference = useRef("");

  const twClassname = twMerge("flex w-full justify-center", className);

  const links = useMemo(
    () =>
      quickLinks.links?.["@nodes"]?.map((node) => quickLinks.links?.[node]) ??
      [],
    [quickLinks.links],
  );

  const filteredLinks = useMemo(
    () =>
      inputValue
        ? links.filter((link) =>
            link?.label.toLowerCase().includes(inputValue.toLowerCase()),
          )
        : links,
    [links, inputValue],
  );

  const quickLinkNodes = useMemo(
    () =>
      filteredLinks.map((link) => (
        <ListBoxItem
          className="
            flex min-h-8 cursor-pointer! items-center px-6
            text-foreground-primary

            hover:bg-background-primary hover:text-foreground-on-primary

            data-focused:bg-background-primary
            data-focused:text-foreground-on-primary
          "
          id={link?.href}
          key={link?.href}
          textValue={link?.label}
        >
          <Link className="block w-full" href={link?.href ?? ""}>
            {link?.label}
          </Link>
        </ListBoxItem>
      )),
    [filteredLinks],
  );

  const searchHref = appendSearchQueryString({
    href: primaryLink.href,
    search: inputValue,
  });

  return (
    <div className={twClassname} data-fs-element="input-search">
      <InputComboBox
        allowsCustomValue
        inputProperties={
          theme === "dark"
            ? {
                className: "focus-within:border-orange",
                inputClassName: "focus:!border-orange",
              }
            : undefined
        }
        inputValue={inputValue}
        label={labels.placeholder}
        onInputChange={setInputValue}
        onKeyDown={(event: KeyboardEvent) => {
          if (!(event.key === "Enter" && event.target instanceof Element)) {
            return;
          }

          activeDescendantOnEnterReference.current = event.target.getAttribute(
            "aria-activedescendant",
          );
          inputValueOnEnterReference.current = inputValue;
        }}
        onKeyUp={(event: KeyboardEvent) => {
          if (event.key !== "Enter") {
            return;
          }
          const activeDescendant = activeDescendantOnEnterReference.current;
          const capturedInputValue = inputValueOnEnterReference.current;
          activeDescendantOnEnterReference.current = null;
          inputValueOnEnterReference.current = "";
          if (activeDescendant) {
            router.push(activeDescendant);
          } else if (capturedInputValue) {
            router.push(
              appendSearchQueryString({
                href: primaryLink.href,
                search: capturedInputValue,
              }),
            );
          }
        }}
      >
        {quickLinkNodes.length > 0 && (
          <ListBoxSection>
            <Header>{quickLinks.label}</Header>

            {quickLinkNodes}
          </ListBoxSection>
        )}
      </InputComboBox>

      <PairedButton
        aria-label={primaryLink.label}
        as={Link}
        className="cursor-pointer"
        href={searchHref}
        variant={theme === "light" ? "primary" : "secondary"}
      />
    </div>
  );
};
